import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { Group, Text, Button } from '@mantine/core';
import { MealPlanView } from '../components/MealPlanning/MealPlanView';
import { MealPlan } from '../types/mealPlan';
import { formatDateRange } from '../utils/mealPlanUtils';
import { useMealPlanning } from '../hooks/useMealPlanning';
import { useAuth } from '../hooks/useAuth';
import { useAppUser } from '../hooks/useAppUser';
import { buildPlanPath } from '../utils/routeHelpers';
import { setPageCache } from '../utils/pageCache';
import { nextMealPlansFetch, isLatestMealPlansFetch } from '../utils/mealPlansFetchGuard';
import { restoreMealPlanInCache } from '../utils/mealPlansCache';

export const MealPlanPage = () => {
  const { userId, planId, slugWithId } = useParams<{ userId?: string; planId?: string; slugWithId?: string }>();
  const navigate = useNavigate();
  const { deleteMealPlan, getUserMealPlans } = useMealPlanning();
  const { user } = useAuth();
  const { user: appUser } = useAppUser();
  const [isDeleting, setIsDeleting] = useState(false);

  // The dashboard and history page seed from — and subscribe to — the shared
  // plans cache. A restore performed from this page's undo toast must write
  // it, or a mounted dashboard keeps showing the list without the restored
  // plan until a full reload. Runs after navigation; this closure outlives
  // the page.
  const refreshPlansCache = async () => {
    const uid = appUser?.uid || user?.uid;
    if (!uid) return;
    const fetchToken = nextMealPlansFetch();
    const plans = await getUserMealPlans();
    // A newer list fetch superseded this one — drop the stale response.
    if (!isLatestMealPlansFetch(fetchToken)) return;
    setPageCache(`meal-plans:${uid}`, plans);
  };

  // For friendly URLs (/plan/:slugWithId), don't fall back to user?.uid
  // so the hook uses the /api/plan/:slugWithId endpoint instead of /api/u/:userId/plan/:planId
  const effectiveUserId = userId || (slugWithId ? undefined : user?.uid);

  const handleBack = () => {
    navigate(-1);
  };

  const handleDeleteMealPlan = async (mealPlan: MealPlan) => {
    setIsDeleting(true);
    
    try {
      const success = await deleteMealPlan(mealPlan.id);
      
      if (success) {
        const dateRange = formatDateRange(mealPlan);
        const notificationId = `delete-meal-plan-${mealPlan.id}`;

        // Navigate to dashboard first
        navigate('/');
        refreshPlansCache();
        
        // Show undo notification with loading state support
        const UndoButton = () => {
          const [isUndoing, setIsUndoing] = useState(false);
          
          return (
            <Button
              size="xs"
              variant="subtle"
              color="orange"
              loading={isUndoing}
              onClick={async () => {
                setIsUndoing(true);
                try {
                  const response = await fetch(`${process.env.REACT_APP_API_URL}/api/meals/plans/${mealPlan.id}/undo`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${await user?.getIdToken()}`,
                    },
                  });
                  
                  if (response.ok) {
                    notifications.hide(notificationId);
                    // Put the plan straight back into the shared list cache
                    // (we hold the full object), so the dashboard shows it
                    // again without depending on a refetch round-trip.
                    const uid = appUser?.uid || user?.uid;
                    if (uid) {
                      restoreMealPlanInCache(uid, mealPlan);
                    }
                    // Navigate back to the restored meal plan
                    navigate(buildPlanPath(mealPlan, user?.uid || ''));
                    refreshPlansCache();
                  } else {
                    throw new Error('Failed to restore meal plan');
                  }
                } catch (error) {
                  notifications.show({
                    title: 'Error',
                    message: 'Failed to restore meal plan',
                    color: 'red',
                  });
                } finally {
                  setIsUndoing(false);
                }
              }}
            >
              Undo
            </Button>
          );
        };
        
        // Show undo notification
        notifications.show({
          id: notificationId,
          title: 'Meal plan deleted',
          message: (
            <Group>
              <Text size="sm">{`Meal plan for ${dateRange} has been deleted`}</Text>
              <UndoButton />
            </Group>
          ),
          color: 'orange',
          autoClose: 5000,
          withCloseButton: true,
        });
      } else {
        notifications.show({
          title: 'Error',
          message: 'Failed to delete meal plan. Please try again.',
          color: 'red',
        });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  if ((!planId && !slugWithId) || (!effectiveUserId && !slugWithId)) {
    navigate('/');
    return null;
  }

  return (
    <MealPlanView
      userId={effectiveUserId || ''}
      planId={planId}
      slugWithId={slugWithId}
      onBack={handleBack}
      onDelete={handleDeleteMealPlan}
      isDeleting={isDeleting}
    />
  );
};
